import { useState } from "react";
import { alpha, useTheme } from "@mui/material/styles";
import {
  Box,
  Button,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from "@mui/material";
import { Archive as ArchiveIcon } from "@mui/icons-material";
import BulkArchiveResultAlert from "./BulkArchiveResultAlert";
import InitiativeDialog from "./InitiativeDialog";
import ListRow from "./ListRow";
import SelectionCheckboxCell from "./SelectionCheckboxCell";
import { BULK_ARCHIVE_MAX_IDS } from "../constants/bulkArchiveLimit";
import { CHIP_COLUMN_WIDTHS } from "../constants/chipColumnWidths";
import useInitiativeSelection from "../hooks/useInitiativeSelection";
import { useLookups } from "../hooks/useLookups";
import { useInitiativeSort } from "../hooks/useInitiativeSort";
import { useSelectionSet } from "../hooks/useSelectionSet";
import { getValueComparators } from "../utils/valueComparators";
import { SORT_OPTIONS } from "../constants/sortOptions";

export default function ListView({ initiatives, mutations, onBulkArchive }) {
  const theme = useTheme();
  const lookups = useLookups();
  const valueComparators = getValueComparators(lookups);
  const selection = useInitiativeSelection(mutations);
  const { sortColumn, setSortColumn, sortDir, setSortDir } =
    useInitiativeSort();

  const handleSort = (col) => {
    if (sortColumn === col) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(col);
      setSortDir("asc");
    }
  };

  const sorted = [...initiatives].sort((a, b) => {
    if (!sortColumn) return 0;
    const dir = sortDir === "asc" ? 1 : -1;
    const compare = valueComparators[sortColumn];
    return compare ? dir * compare(a[sortColumn], b[sortColumn]) : 0;
  });

  const selectableIds = sorted
    .filter((item) => !item.archived)
    .map((item) => item.id);
  const {
    selected: selectedIds,
    setSelected: setSelectedIds,
    visibleSelected: visibleSelectedIds,
    allSelected,
    someSelected,
    limitReached,
    toggleAll,
    toggleOne,
  } = useSelectionSet(selectableIds, BULK_ARCHIVE_MAX_IDS);

  const [bulkArchiveResult, setBulkArchiveResult] = useState(null);

  const handleBulkArchive = async () => {
    const result = await onBulkArchive(visibleSelectedIds);
    if (result) {
      setSelectedIds(new Set());
      setBulkArchiveResult(result);
    }
  };

  const styles = {
    root: { p: 3 },
    tableContainer: { bgcolor: "background.tableHeader" },
    headerRow: {
      "& th": {
        fontWeight: 700,
        bgcolor: "background.tableHeader",
        color: "background.tableHeaderText",
        fontSize: "0.7rem",
        textTransform: "uppercase",
        letterSpacing: "0.07em",
      },
    },
    activeSortHeader: {
      bgcolor: alpha(theme.palette.primary.main, 0.1),
    },
    sortLabel: {
      color: "inherit",
      "&:hover": { color: "primary.main" },
      "&:hover .MuiTableSortLabel-icon": {
        color: `${theme.vars.palette.primary.main} !important`,
      },
      "&.Mui-active": { color: "primary.main" },
      "& .MuiTableSortLabel-icon": { color: "inherit !important" },
    },
    columnWidths: {
      title: {
        fontWeight: 600,
        maxWidth: 320,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      },
      product: {
        maxWidth: 220,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      },
      owner: {
        maxWidth: 200,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      },
    },
    tableBody: { bgcolor: "background.tableBody" },
    bulkBar: {
      px: 3,
      pt: 1,
      pb: 1.5,
      gap: 2,
      alignItems: "center",
      bgcolor: alpha(theme.palette.primary.main, 0.08),
      mb: `-${theme.shape.borderRadius}px`,
    },
    bulkLabel: { fontWeight: 500, flex: 1 },
    bulkLimitHint: { color: "background.textMuted", fontWeight: 400 },
    bulkBtn: { textTransform: "none" },
  };

  return (
    <Box sx={styles.root}>
      {onBulkArchive && (
        <Stack direction="row" sx={styles.bulkBar}>
          <Typography variant="body2" sx={styles.bulkLabel}>
            {visibleSelectedIds.length} selected
            {limitReached && (
              <Typography
                component="span"
                variant="caption"
                sx={styles.bulkLimitHint}
              >
                {" "}
                — selection limit {BULK_ARCHIVE_MAX_IDS} has been reached
              </Typography>
            )}
          </Typography>
          <Button
            size="small"
            variant="contained"
            color="archived"
            startIcon={<ArchiveIcon />}
            onClick={handleBulkArchive}
            disabled={visibleSelectedIds.length === 0}
            sx={styles.bulkBtn}
          >
            Archive
          </Button>
        </Stack>
      )}
      <TableContainer
        component={Paper}
        elevation={2}
        sx={styles.tableContainer}
      >
        <Table size="medium" stickyHeader>
          <TableHead>
            <TableRow sx={styles.headerRow}>
              {onBulkArchive && (
                <SelectionCheckboxCell
                  checked={allSelected}
                  indeterminate={someSelected}
                  onChange={toggleAll}
                  disabled={selectableIds.length === 0}
                />
              )}
              <TableCell>Title</TableCell>
              <TableCell>Product</TableCell>
              <TableCell>Owner</TableCell>
              {SORT_OPTIONS.filter((option) => option.value !== "default").map(
                (column) => (
                  <TableCell
                    key={column.value}
                    sortDirection={
                      sortColumn === column.value ? sortDir : false
                    }
                    sx={[
                      CHIP_COLUMN_WIDTHS[column.value],
                      sortColumn === column.value
                        ? styles.activeSortHeader
                        : null,
                    ]}
                  >
                    <TableSortLabel
                      active={sortColumn === column.value}
                      direction={sortColumn === column.value ? sortDir : "asc"}
                      onClick={() => handleSort(column.value)}
                      sx={styles.sortLabel}
                    >
                      {column.label}
                    </TableSortLabel>
                  </TableCell>
                ),
              )}
            </TableRow>
          </TableHead>
          <TableBody sx={styles.tableBody}>
            {sorted.map((item) => (
              <ListRow
                key={item.id}
                item={item}
                widths={styles.columnWidths}
                onClick={() => selection.select(item)}
                leading={
                  onBulkArchive && (
                    <SelectionCheckboxCell
                      checked={selectedIds.has(item.id)}
                      onChange={() => toggleOne(item.id)}
                      disabled={limitReached && !selectedIds.has(item.id)}
                      hidden={item.archived}
                      stopClickPropagation
                    />
                  )
                }
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <InitiativeDialog selection={selection} />
      <BulkArchiveResultAlert
        result={bulkArchiveResult}
        onClose={() => setBulkArchiveResult(null)}
      />
    </Box>
  );
}
